import Head from 'next/head';
import { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import {
  ApiJobDetail,
  fetcher,
  isJobDetailOk,
  ApiJobDetailOk,
  ApiJobUserDetailOk,
  isJobListCardsOk,
  isJobUserDetailOk,
  ApiJobListCards,
  ApiJobUserDetail,
  ApiBreadcrumbs,
  isBreadcrumbsOk,
  ApiBreadcrumbsOk,
} from '@utils/data-fetching';
import { ParsedUrlQuery } from 'querystring';
import { useRouter } from 'next/router';
import { getCanonicalUrl, getI18nLinkHref } from '@services/i18nPaths';
import { JobDetail } from '@components/templates/job/detail/JobDetail';
import { JobSeoWidget } from '@components/templates/job/SeoWidget';
import { getJobTitle, getJobMetaDescription } from '@utils/job';
import { JobQueryParams } from '@type/job-params';
import { useEffect, useMemo, ReactNode } from 'react';
import { JobTrackingContext } from '@components/contexts/JobTrackingContext';
import { gsspCheckCookieAuth, gsspGetLocale, gsspRedirect } from '@utils/gssp';
import sendGoogleTagEvent from '@hooks/useSendGoogleTagEvent';
import { Breadcrumbs } from '@components/templates/job/Breadcrumbs';
import { JOB_DETAIL_SIMILAR_JOBS_LIMIT } from 'src/constants';
import { useSwrJobDetail, useSwrJobUserDetail } from '@hooks/useSwrApi/endpoints/job-operations';
import { useAppSelector } from '@store/index';
import { getUserHeaderInfo } from '@store/reducers/userSelector';
import ogImageEn from '@images/social-media/ai_on_your_side_en.png';
import ogImageRo from '@images/social-media/ai_on_your_side_ro.png';
import { getJobBulkApplyFeed } from '@store/reducers/jobSelector';
import NiceModal from '@ebay/nice-modal-react';
import { BulkApplyModal } from '@components/templates/job/apply/BulkApplyModal';
import { LayoutDefault } from '@components/layout/Layouts/LayoutDefault';

// Interface
// ******************************************
interface JobDetailPageParams extends ParsedUrlQuery {
  jobSlug: string,
}
interface JobDetailPageProps {
  job: ApiJobDetail;
  jobUser: ApiJobUserDetail;
  locale: Locale;
  similarJobs: ApiSchema<'JobResult'> | null;
  jobListCards: ApiSchema<'CursorJobResult'> | null;
  breadcrumbs: ApiBreadcrumbsOk | null;
  slug: string;
}

/**
 * @description Job Detail page
 * - **job**: ApiJobDetailOk | null - is the job detail object.
 * - **jobUser**: ApiJobUserDetail - is the job user detail object.
 * - **locale**: Locale - is the locale.
 * - **similarJobs**: ApiSchema<'JobResult'> | null - Similar jobs list.
 * - **prevNextJobs**: ApiPrevNextJobOk | null - Navigation object. It contains prev / next jobs for in page navigation (not modal).
 * - **breadcrumbs**: ApiBreadcrumbsOk | null - Breadcrumb for job.
 */
const JobDetailPage = (props: JobDetailPageProps) => {
  // Destructure props
  const {
    job: serverJob,
    jobUser,
    locale,
    similarJobs,
    jobListCards,
    breadcrumbs,
    slug: jobSlug,
  } = props;


  // Get header info data
  // *************************************
  const headerInfoData = useAppSelector(getUserHeaderInfo);
  // SWR: Job Detail
  // ************************************
  const {
    data: jobDetail,
  } = useSwrJobDetail(jobSlug ?? '', locale, {}, serverJob);

  // SWR: Job User Detail
  // If the user is authenticated, then we need the job user detail in order to get the liked, disliked, applied props and skills matching.
  // ************************************
  const {
    data: jobUserDetail, mutate,
  } = useSwrJobUserDetail(jobSlug ?? '', !!headerInfoData, locale, jobUser);

  const job = useMemo(() => (
    { ...jobDetail, ...isJobUserDetailOk(jobUserDetail) ? jobUserDetail : {} }
  ), [jobDetail, jobUserDetail]) as ApiJobDetailOk & ApiJobUserDetailOk;

  // Translation
  const { t } = useTranslation('common');

  // Next router
  const { asPath, query } = useRouter();

  // Title & Meta description & OG Tags
  // *****************************************
  const title = getJobTitle(job, t);
  const metaDescription = getJobMetaDescription(job, t);
  const ogTitle = job ? `${job.title}${job.employer ? ` , ${job.employer.employerName}` : ''}` : title;

  // OG Tags
  const ogUrl = getCanonicalUrl(asPath);
  const facebookAppId = process.env.NEXT_PUBLIC_FACEBOOK_APP_ID ?? '';
  const localizedOgImage = locale === 'ro' ? ogImageRo : ogImageEn;
  const ogImageUrl = localizedOgImage.src.startsWith('http') ? localizedOgImage.src : `${process.env.NEXT_PUBLIC_BASE_URL}${localizedOgImage.src}`;

  // Tracking
  // *****************************************
  useEffect(() => {
    sendGoogleTagEvent('job_detail', 'Custom');
  }, [query, job]);

  // Data for the Job Tracking Context.
  // ***********************************************
  const jobTrackingContext = useMemo(() => ({
    requestId: typeof query[JobQueryParams.TR_REQUEST_ID] === 'string' ? query[JobQueryParams.TR_REQUEST_ID] : undefined,
  }), [query]);

  const bulkApplyFeed = useAppSelector(getJobBulkApplyFeed);

  useEffect(() => {
    if (bulkApplyFeed?.length) {
      void NiceModal.show(BulkApplyModal, { jobList: bulkApplyFeed, locale });
    }
  }, [bulkApplyFeed, locale]);

  return (
    <>

      {/* HEAD - title, meta, etc */}
      <Head>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />

        {/* Open Graph */}
        <meta property="og:url" content={ogUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="fb:app_id" content={facebookAppId} />
        <meta property="og:image" content={job?.ogImgUrl || ogImageUrl} />

        {!job?.active && (
          <meta name="robots" content="noindex" />
        )}
      </Head>

      {/* Job Detail Page */}
      <div className="bg-surface-100">

        {/* Breadcrumbs */}
        <div className="container">
          {breadcrumbs && <Breadcrumbs className="!mt-6" breadcrumbs={breadcrumbs} />}
        </div>

        <div className="container mt-4 px-0 pb-4 sm:mt-6 sm:px-2.5 sm:pb-8">
          {/* Job Detail */}
          <JobTrackingContext.Provider value={jobTrackingContext}>
            <JobDetail
              mutateUserDetail={() => { void mutate(); }}
              job={job}
              isSinglePage
              locale={locale}
              similarJobs={similarJobs}
              apiPrevNextJobs={{
                ...(Number(query.offset) > 0 && {
                  prevJob: {
                    slug: '',
                    title: '',
                    jobId: 0,
                    offset: Number(query.offset) - 1,
                    url: '',
                  },
                }),
                nextJob: {
                  slug: jobListCards?.items?.[0]?.slug ?? '',
                  title: jobListCards?.items?.[0]?.title ?? '',
                  jobId: Number(jobListCards?.items?.[0]?.id ?? 0),
                  offset: Number(query.offset) + 1,
                  url: `${getI18nLinkHref(
                    `/job/${jobListCards?.items?.[0]?.slug}?cursor=${jobListCards?.nextCursor}&offset=${Number(query.offset ?? '0') + 1}`,
                    locale,
                  )}`,
                },
              }}
              mode="default"
            />
          </JobTrackingContext.Provider>

          {/* SEO Widget */}
          {job && (job.seoCities || job.seoDomains) && (
            <JobSeoWidget
              seoCities={job.seoCities}
              seoDomains={job.seoDomains}
              seoIsAbroad={job.seoIsAbroad}
              className="!rounded-none sm:!rounded-md"
            />
          )}
        </div>

      </div>
    </>
  );
};

// STATIC PROPS ************************************************************************************
// *************************************************************************************************
export const getServerSideProps: GetServerSideProps<JobDetailPageProps, JobDetailPageParams> = async (context) => {
  // Destructure context
  const {
    req,
    params,
    query,
  } = context;

  const locale = gsspGetLocale(params);

  // Get route param
  // *****************************************
  const jobSlug = params?.jobSlug;

  if (!jobSlug) {
    return gsspRedirect({ url: '/' });
  }

  // Build search params object to for Prev Next Job Request
  // *****************************************
  const prevNextJobSearchParams = new URLSearchParams();

  // Add SEARCH_ID parameter (`sid`) from query, if present.
  if (typeof query[JobQueryParams.SEARCH_ID] === 'string') {
    prevNextJobSearchParams.append(JobQueryParams.SEARCH_ID, query[JobQueryParams.SEARCH_ID]);
  }

  if (typeof query.cursor === 'string') {
    prevNextJobSearchParams.append('cursor', query.cursor);
  }

  // Add TR_REQUEST_ID parameter from query, if present.
  if (typeof query[JobQueryParams.TR_REQUEST_ID] === 'string') {
    prevNextJobSearchParams.append(JobQueryParams.TR_REQUEST_ID, query[JobQueryParams.TR_REQUEST_ID]);
  }

  prevNextJobSearchParams.append('limit', '1');

  // Check if the user is authenticated
  // *****************************************
  const isAuthenticated = await gsspCheckCookieAuth({ context }) as boolean;

  // Fetch data from server
  // If the user is authenticated, then we need the job user detail request in order to get the liked, disliked, applied props.
  // *****************************************
  const [
    jobDetailResponse,
    jobUserDetailResponse,
    similarJobsResponse,
    jobListCardsResponse,
    breadcrumbsResponse,
  ] = await Promise.all([
    fetcher<ApiJobDetail>(`/v1/job-operation/${jobSlug}/detail?includeSeoData=1`, locale, {
      ssrClientHeaders: req.headers,
    }),
    isAuthenticated ? (
      fetcher<ApiJobUserDetail>(`/v1/job-operation/${jobSlug}/user-detail`, locale, {
        ssrClientHeaders: req.headers,
      })
    ) : Promise.resolve(),
    fetcher<ApiJobListCards>(`/v3/jobs/similar?jobSlug=${jobSlug}&limit=${JOB_DETAIL_SIMILAR_JOBS_LIMIT}`, locale, {
      ssrClientHeaders: req.headers,
    }),
    fetcher<ApiSchema<'CursorJobResult'>[]>(`/v2/jobs?${prevNextJobSearchParams.toString()}`, locale, {
      ssrClientHeaders: req.headers,
    }),
    !isAuthenticated ? (
      fetcher<ApiBreadcrumbs>(`/v3/job/${jobSlug}/breadcrumb`, locale, {
        ssrClientHeaders: req.headers,
      })
    ) : Promise.resolve(),
  ]);

  // Return props
  // *********************************
  const job: (ApiJobDetailOk & ApiJobUserDetailOk) | null = isJobDetailOk(jobDetailResponse) ? jobDetailResponse : null;

  // If job is null, then return a `notFound` error
  if (!job) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      locale,
      slug: jobSlug,
      job,
      jobUser: isJobUserDetailOk(jobUserDetailResponse) ? jobUserDetailResponse : {},
      similarJobs: isJobListCardsOk(similarJobsResponse) ? similarJobsResponse : null,
      jobListCards: isJobListCardsOk(jobListCardsResponse as ApiJobListCards) ? jobListCardsResponse as ApiSchema<'CursorJobResult'> : null,
      breadcrumbs: isBreadcrumbsOk(breadcrumbsResponse) ? breadcrumbsResponse : null,
    },
  };
};

// LAYOUT *****************************************************************************************
JobDetailPage.getLayout = (page: ReactNode) => (
  <LayoutDefault>
    {page}
  </LayoutDefault>
);

// EXPORT ******************************************************************************************
export default JobDetailPage;
